import { Form, FormApi, getLanguage, If, onMount, reactive } from 'cdui-js';
import {
  changeFile,
  lastProcess,
  nextProcess,
  OTHER_BROKER_BANK_CODE,
  pState,
  setFormApi,
  setFormValidateFunc,
} from '../../store';
import { UploadProof } from '../../UploadProof';
import { SelectBox } from '../../../../components/SelectBox';
import { LastNextBtns } from '../../components/LastNextBtns';
import { isOpenProgressReEdit } from '../../reEditMode';
import { aesEncryptHexByPass } from '../../../../../../lib/crypto';
import { commonState } from '../../../../../../stores/common';
import {
  bingBankAcc,
  composeSignatureOnAttachment,
  fetchOpenAccountMarketBanks,
} from '../../../../../../api/open-account';
import { isOpenAccountAppWebView, waitForOpenAccountBootstrap } from '../../openAccountSession';
import { FieldReEditHint } from '../../components/FieldReEditHint';
import { OpenFormItem } from '../../components/OpenFormItem';
import { DiFiInput } from '../../../../../../components/DiFi/DiFiInput';
import { showToast, showToastError } from '../../../../components/Toast';
import { getAuthFileBlob } from '../../../../../../lib/authFileBlob';
import i18n from '../../../../../../i18n';

/**
 * /2go/market/banks：
 * bankCodeForTxn（如 004）→ 保存字段 bankCode
 * bankCode（如 HSBC）→ 保存字段 brokerBankCode
 */
const toBankList = (res: any): any[] => {
  if (Array.isArray(res)) return res;
  if (Array.isArray(res?.data)) return res.data;
  if (Array.isArray(res?.list)) return res.list;
  return [];
};

const marketBankLabel = (row: any) => {
  const lang = String(getLanguage() || 'zh-CN');
  const field =
    lang === 'en' || lang === 'en-US' || lang.startsWith('en-')
      ? 'bankNameEN'
      : lang === 'zh-TW' || lang === 'zh-HK' || lang === 'zh-Hant'
        ? 'bankNameTC'
        : 'bankNameSC';
  return String(row?.[field] || row?.bankName || '').trim();
};

const bankState = reactive({ list: [] as any[] });
let marketBanksTask: Promise<any[]> | null = null;

export const loadMarketBanks = () => {
  if (bankState.list.length) return Promise.resolve(bankState.list);
  if (!marketBanksTask) {
    marketBanksTask = (async () => {
      // 首帧 onMount 早于 Bootstrap。App 开户页必须等 authorization 写入后再请求，避免空 token 打到网关。
      try {
        const session = await waitForOpenAccountBootstrap();
        if (isOpenAccountAppWebView() && !session?.authorization) return bankState.list;
      } catch {
        if (isOpenAccountAppWebView()) return bankState.list;
      }
      const res = await fetchOpenAccountMarketBanks();
      const list = toBankList(res);
      if (list.length) bankState.list = list;
      return bankState.list;
    })().finally(() => {
      marketBanksTask = null;
    });
  }
  return marketBanksTask;
};

const findMarketBank = (txnCode: string) =>
  bankState.list.find((item) => String(item?.bankCodeForTxn ?? '') === txnCode);

/** 已有交易行号、还没有券商编码时，用市场银行列表补上，避免回填后直接保存漏字段 */
export const fillOpenAccountBrokerBankCode = async () => {
  if (String(pState.bank.brokerBankCode || '').trim()) return;
  try {
    await loadMarketBanks();
  } catch {
    return;
  }
  const txnCode = String(pState.bank.bankCode || '');
  if (txnCode === OTHER_BROKER_BANK_CODE) return;
  const row = findMarketBank(txnCode);
  if (row?.bankCode) pState.bank.brokerBankCode = String(row.bankCode);
};

const isOtherOpenAccountBank = (bank: { bankCode?: string; brokerBankCode?: string } = pState.bank) =>
  String(bank?.brokerBankCode || '') === OTHER_BROKER_BANK_CODE ||
  String(bank?.bankCode || '') === OTHER_BROKER_BANK_CODE;

/**
 * 字典命中取当前语言名称。OTHER 或未命中时用 bankName。
 * 渲染期读取 bankState.list，列表返回后确认页会跟着更新。
 */
export const resolveOpenAccountBankDisplay = (row: {
  bankCode?: string;
  brokerBankCode?: string;
  bankName?: string;
}) => {
  const fallback = String(row?.bankName ?? '').trim();
  const broker = String(row?.brokerBankCode ?? '').trim();
  if (broker === OTHER_BROKER_BANK_CODE) return fallback;
  const txn = String(row?.bankCode ?? '').trim();
  const hit = bankState.list.find((item) => {
    if (broker && String(item?.bankCode ?? '') === broker) return true;
    if (txn && txn !== OTHER_BROKER_BANK_CODE && String(item?.bankCodeForTxn ?? '') === txn) return true;
    return false;
  });
  if (!hit) return fallback;
  return marketBankLabel(hit) || fallback;
};

/** 组绑定入参：其他银行 bankCode 留空、brokerBankCode=OTHER；已有银行补上当前语言名称。账号仍是明文，由调用方加密。 */
export const buildOpenAccountBankBindPayload = async () => {
  try {
    await loadMarketBanks();
  } catch {
    // 字典失败时已有银行退回已保存的 bankName
  }
  await fillOpenAccountBrokerBankCode();
  const other = isOtherOpenAccountBank();
  if (other) {
    const name = String(pState.bank.bankName || '').trim();
    pState.bank.bankName = name;
    if (!name) {
      showToast(i18n.Common.FormRequired);
      return null;
    }
  }
  const data = JSON.parse(JSON.stringify(pState.bank));
  if (other) {
    data.bankCode = '';
    data.brokerBankCode = OTHER_BROKER_BANK_CODE;
    data.bankName = String(data.bankName || '').trim();
  } else {
    const row = findMarketBank(String(data.bankCode || ''));
    data.bankName = (row ? marketBankLabel(row) : '') || String(data.bankName || '').trim();
    if (row?.bankCode) data.brokerBankCode = String(row.bankCode);
  }
  return data;
};

const getBankOptions = () => {
  const options: { value: string; label: string }[] = [];
  for (const row of bankState.list) {
    const value = String(row?.bankCodeForTxn ?? '').trim();
    const label = marketBankLabel(row);
    if (!value || !label || value === OTHER_BROKER_BANK_CODE) continue;
    options.push({ value, label });
  }
  options.push({ value: OTHER_BROKER_BANK_CODE, label: i18n.OpenProgress.Transfer.Bank.Other });
  return options;
};

export const AddBankAccount = (props) => {
  const B = i18n.OpenProgress.Transfer.Bank;
  let formApi: FormApi = null;

  const getBankImg = () => {
    const file = pState.attachmentList.find((i) => i.attachType === 'bank_prove');
    return file?.fileUrl;
  };

  const validateProof = async () => {
    if (!getBankImg()) {
      showToast(B.ToastProofRequired);
      return false;
    }
    return true;
  };

  onMount(() => {
    setFormValidateFunc({
      callback: validateProof,
    });
    // 与上一页「请选择存入币种」对齐（depositCurrency：HKD / USD）
    if (pState.currency.accountType) {
      pState.bank.bankAccountType = pState.currency.accountType;
    }
    // 账户姓名：有本步历史则保留；否则英文名(名+姓)优先，无英文再中文
    if (!(pState.bank.bankAccountName || '').trim()) {
      const en = (pState.uploadLicForm.pinyinName || '').trim();
      const cn = (pState.uploadLicForm.fullName || '').trim();
      pState.bank.bankAccountName = en || cn || '';
    }
    // 下拉依赖市场银行字典；驳回重进时 brokerBankCode 已有（含 OTHER），
    // fillOpenAccountBrokerBankCode 会提前 return，不能只靠它拉列表。
    void loadMarketBanks()
      .then(() => fillOpenAccountBrokerBankCode())
      .catch(() => {});
  });

  return (
    <div class="padding-x-L margin-y-XXL">
      <div class="font-s-L font-bold color-D margin-b-XXS font-bold">{B.Title}</div>

      <div class="color-error">{B.SameNameWarning}</div>
      <div>{B.NoThirdPartyNote}</div>

      <div class="color-D font-s font-bold margin-t-L">{B.IdentitySection}</div>

      <Form
        data={pState.bank}
        align="top"
        rules={{}}
        api={(api) => {
          formApi = api;
          setFormApi(api);
        }}
      >
        <OpenFormItem label={B.BankName} field="bankCode" required class="margin-t-L">
          <SelectBox
            title={B.BankListTitle}
            value={pState.bank.bankCode}
            options={getBankOptions()}
            change={(e) => {
              const code = String(e ?? '');
              if (code === OTHER_BROKER_BANK_CODE) {
                if (pState.bank.brokerBankCode !== OTHER_BROKER_BANK_CODE) pState.bank.bankName = '';
                pState.bank.bankCode = OTHER_BROKER_BANK_CODE;
                pState.bank.brokerBankCode = OTHER_BROKER_BANK_CODE;
                return;
              }
              const row = findMarketBank(code);
              pState.bank.bankCode = code;
              pState.bank.brokerBankCode = String(row?.bankCode ?? '');
              pState.bank.bankName = '';
            }}
          />
        </OpenFormItem>

        <If when={pState.bank.brokerBankCode === OTHER_BROKER_BANK_CODE}>
          <OpenFormItem label="" field="bankName" required class="margin-t-S">
            <DiFiInput
              shellClass="f-input-2"
              inputKind="text"
              maxLength={80}
              placeholder={B.OtherNamePlaceholder}
              value={pState.bank.bankName}
              onUpdateValue={(v) => (pState.bank.bankName = v)}
            />
          </OpenFormItem>
        </If>

        <OpenFormItem label={B.AccountName} field="bankAccountName" required class="margin-t-L">
          <DiFiInput
            shellClass="f-input-2"
            inputKind="text"
            maxLength={80}
            placeholder={i18n.OpenProgress.Common.PlaceholderEnter}
            value={pState.bank.bankAccountName}
            onUpdateValue={(v) => (pState.bank.bankAccountName = v)}
          />
        </OpenFormItem>

        <OpenFormItem label={B.AccountNo} field="bankAccountNo" required class="margin-t-L">
          <DiFiInput
            shellClass="f-input-2"
            inputKind="bank-account"
            maxLength={50}
            placeholder={i18n.OpenProgress.Common.PlaceholderEnter}
            value={pState.bank.bankAccountNo}
            onUpdateValue={(v) => (pState.bank.bankAccountNo = v)}
          />
        </OpenFormItem>

        <OpenFormItem label={B.AccountType} field="bankAccountType" required class="margin-t-L">
          <SelectBox
            title={B.AccountType}
            value={pState.bank.bankAccountType}
            dict="depositCurrency"
            change={(e) => {
              pState.bank.bankAccountType = e;
              pState.currency.accountType = e;
            }}
          />
        </OpenFormItem>
      </Form>

      <div class="margin-t-L">
        <div class="color-D font-s font-bold">{B.UploadProofTitle}</div>
        <div class="margin-t-XXS margin-b-S">{B.UploadProofDesc}</div>

        <UploadProof
          useFor="bank_prove"
          busId={pState.id}
          img={getBankImg()}
          change={({ url }) => {
            changeFile('bank_prove', url);
          }}
        />
        <FieldReEditHint field="bank_prove" />
      </div>

      <If when={!isOpenProgressReEdit(props)}>
        <LastNextBtns
          last={() => {
            lastProcess();
          }}
          next={async () => {
            if (pState.bank.brokerBankCode === OTHER_BROKER_BANK_CODE) {
              pState.bank.bankName = String(pState.bank.bankName || '').trim();
            }
            const valid = await formApi.validate();
            if (!valid) return;
            if (!(await validateProof())) return;

            const data = await buildOpenAccountBankBindPayload();
            if (!data) return;
            console.log('[encryptKey][beforeEncrypt][bankAccountNo]', {
              encryptKey: commonState.encryptKey || '(empty)',
              plainLen: String(data.bankAccountNo || '').length,
            });
            data.bankAccountNo = aesEncryptHexByPass(data.bankAccountNo, commonState.encryptKey);
            await bingBankAcc({ ...data, progress: 19 });

            // 非大陆：绑卡成功后再把 signature_sticker 贴到开户 PDF
            const isMainland = pState.uploadLicForm.idCardType === 'MAINLAND_CHINA';
            if (!isMainland) {
              try {
                const sticker = pState.attachmentList.find(
                  (i) => i.attachType === 'signature_sticker' && i.fileUrl,
                );
                if (!sticker?.fileUrl) {
                  showToast(i18n.OpenProgress.Transfer.Signature.ToastCompleteFirst);
                  return;
                }
                const blob = await getAuthFileBlob(sticker.fileUrl);
                if (!blob) {
                  showToast(i18n.OpenProgress.Transfer.Signature.ToastCompleteFirst);
                  return;
                }
                const signFile = new File([blob], 'sign.png', {
                  type: blob.type || 'image/png',
                });
                await composeSignatureOnAttachment(pState.id, signFile);
              } catch (e) {
                showToastError(e);
                return;
              }
            }

            nextProcess();
          }}
        />
      </If>
    </div>
  );
};
